import { Link } from 'react-router-dom';

interface Cabin {
  name: string;
  image: string;
  imageAlt: string;
  paragraphs: string[];
  locationNote?: string;
  badges?: string[];
  airbnbUrl: string;
  showInternalLink?: boolean;
}

const CABINS: Cabin[] = [
  {
    name: 'Glen Falls Luxury Cabin',
    image: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/ba0d51886e955561a1eff3aa82f68703.jpeg',
    imageAlt: 'Glen Falls Luxury Cabin exterior — pet-friendly cabin near Glen Falls in Highlands NC',
    paragraphs: [
      "Glen Falls Luxury Cabin in Highlands, NC is a highly rated, pet-friendly retreat located just minutes from Glen Falls and close to many of the area\u2019s most popular hiking trails and waterfalls.",
      "With its peaceful wooded setting and thoughtful design, it offers a comfortable and convenient stay for guests traveling with dogs.",
    ],
    locationNote: "Glen Falls Luxury Cabin is located in Highlands, North Carolina, near popular destinations like Glen Falls, Dry Falls, and Whiteside Mountain.",
    badges: [
      'Consistently rated as a Guest Favorite on Airbnb',
      'One of the most highly rated pet-friendly cabins in Highlands, NC.',
    ],
    airbnbUrl: 'https://www.airbnb.com/rooms/900012473285450950',
    showInternalLink: true,
  },
  {
    name: 'Lark Lodge',
    image: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/aaecc6fc9d1d9ac8bd4441230fe5683d.jpeg',
    imageAlt: 'Lark Lodge pet-friendly cabin surrounded by trees in Highlands NC',
    paragraphs: [
      "Lark Lodge is a stylish, pet-friendly cabin in Highlands featuring a stone fireplace, covered front porch, and a comfortable wooded setting. It sits close to downtown Highlands, making it a convenient base for exploring local trails, restaurants, and shops.",
    ],
    airbnbUrl: 'https://www.airbnb.com/rooms/1617475116852690845',
  },
  {
    name: 'Stag Ridge \u2014 Luxury Highlands Escape',
    image: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/46fa8df15f4549c7bb8d2bc9e3c35336.jpeg',
    imageAlt: 'Stag Ridge luxury cabin on a wooded ridge in Highlands NC — pet friendly',
    paragraphs: [
      "Stag Ridge is a pet-friendly luxury cabin set on a wooded ridge in Highlands, featuring a wraparound deck, indoor stone fireplace, and a tranquil forest setting. A well-appointed retreat for guests seeking comfort and calm in the mountains.",
    ],
    airbnbUrl: 'https://www.airbnb.com/rooms/1180227781183226897',
  },
];

export default function StaySection() {
  return (
    <section className="bg-[#FAFAF8] py-24 px-6 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <p className="text-[#C9A96E] text-xs tracking-[0.3em] uppercase font-jost mb-4">/ Where to Stay</p>
          <h2 className="font-cormorant text-5xl text-[#1A1A18] font-light">
            Pet-Friendly Cabins<br /><em className="italic">in Highlands NC</em>
          </h2>
          <p className="text-[#5C5850] font-jost text-sm leading-relaxed mt-5 max-w-xl">
            These hand-picked pet friendly cabins in Highlands NC welcome dogs and offer comfortable,
            well-located bases for exploring the area&apos;s trails and waterfalls.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CABINS.map((cabin) => (
            <article key={cabin.name} className="bg-white rounded-2xl overflow-hidden flex flex-col">
              <a
                href={cabin.airbnbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-52 overflow-hidden cursor-pointer"
              >
                <img
                  src={cabin.image}
                  alt={cabin.imageAlt}
                  className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                />
              </a>

              <div className="p-7 flex flex-col flex-1">
                <a
                  href={cabin.airbnbUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer group"
                >
                  <h3 className="font-cormorant text-xl text-[#1A1A18] font-medium mb-4 leading-snug group-hover:text-[#2D4A3E] transition-colors">
                    {cabin.name}
                  </h3>
                </a>

                <div className="flex-1">
                  {cabin.paragraphs.map((p, i) => (
                    <p key={i} className="text-[#5C5850] text-sm font-jost leading-relaxed mb-3 last:mb-0">
                      {p}
                    </p>
                  ))}
                  {cabin.locationNote && (
                    <p className="text-[#6B8C7D] text-xs font-jost leading-relaxed mt-3 italic">
                      {cabin.locationNote}
                    </p>
                  )}
                </div>

                {cabin.badges && cabin.badges.length > 0 && (
                  <div className="mt-4 flex flex-col gap-1.5">
                    {cabin.badges.map((badge, i) => (
                      <p key={i} className="text-[#9C9890] text-xs font-jost italic">
                        {badge}
                      </p>
                    ))}
                  </div>
                )}

                <div className="mt-6 flex flex-col gap-3">
                  <a
                    href={cabin.airbnbUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#2D4A3E] hover:bg-[#1e3329] text-white text-xs font-jost tracking-[0.15em] uppercase px-6 py-3 rounded-full transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-external-link-line text-xs" />
                    </div>
                    View on Airbnb
                  </a>

                  {cabin.showInternalLink && (
                    <Link
                      to="/best-luxury-cabins"
                      className="whitespace-nowrap inline-flex items-center justify-center gap-1.5 text-[#6B8C7D] text-xs font-jost hover:text-[#2D4A3E] transition-colors cursor-pointer"
                    >
                      Explore more cabins in Highlands NC
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs" />
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
